import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Heart, Activity, Wind, Brain, Zap, Droplets, TrendingUp,
  ChevronDown, ChevronUp, Loader2, AlertTriangle, CheckCircle,
  Camera, Salad, FlaskConical, Dumbbell, Timer, Clock, BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceArea, ReferenceLine } from 'recharts';
import FaceVitalsScanner from './FaceVitalsScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VITAL_META = {
  heart_rate: { label: 'Heart Rate', unit: 'BPM', icon: Heart, ranges: [60, 100] },
  bp_systolic: { label: 'BP Systolic', unit: 'mmHg', icon: Activity, ranges: [90, 140] },
  bp_diastolic: { label: 'BP Diastolic', unit: 'mmHg', icon: Activity, ranges: [60, 90] },
  respiratory_rate: { label: 'Breathing', unit: '/min', icon: Wind, ranges: [12, 20] },
  spo2: { label: 'SpO2', unit: '%', icon: Droplets, ranges: [95, 100] },
  stress_index: { label: 'Stress', unit: '/100', icon: Brain, ranges: [0, 50] },
  hrv: { label: 'HRV', unit: 'ms', icon: Zap, ranges: [20, 80] },
  cardiac_output: { label: 'Cardiac Out', unit: 'L/min', icon: TrendingUp, ranges: [4, 8] },
};

function getStatus(key, val) {
  const m = VITAL_META[key];
  if (!m || val == null) return 'unknown';
  if (key === 'stress_index') return val <= 35 ? 'normal' : val <= 60 ? 'attention' : 'high';
  if (val >= m.ranges[0] && val <= m.ranges[1]) return 'normal';
  return 'attention';
}

export default function PatientFaceVitalsTab({ patient }) {
  const [showScanner, setShowScanner] = useState(false);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [aiDialog, setAiDialog] = useState(null);
  const [screeningDialog, setScreeningDialog] = useState(null);
  const [showTrends, setShowTrends] = useState(false);
  const [trendRange, setTrendRange] = useState('all');

  const trendData = useMemo(() => {
    if (scans.length < 2) return [];
    let filtered = [...scans];
    if (trendRange !== 'all') {
      const cutoff = new Date();
      if (trendRange === '7d') cutoff.setDate(cutoff.getDate() - 7);
      else if (trendRange === '30d') cutoff.setDate(cutoff.getDate() - 30);
      else if (trendRange === '90d') cutoff.setDate(cutoff.getDate() - 90);
      filtered = filtered.filter(s => new Date(s.created_at) >= cutoff);
    }
    return filtered.reverse().map(s => {
      const d = new Date(s.created_at);
      const v = s.vitals || {};
      const fs = s.face_screening;
      return {
        date: `${d.getDate()}/${d.getMonth() + 1}`,
        heart_rate: v.heart_rate || null,
        spo2: v.spo2 || null,
        stress: v.stress_index || null,
        skin_score: fs?.overall_skin_health_score || null,
      };
    });
  }, [scans, trendRange]);

  const fetchHistory = useCallback(async () => {
    if (!patient?.phone) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/face-vitals/history?patient_phone=${encodeURIComponent(patient.phone)}&limit=20`);
      if (res.ok) { const d = await res.json(); setScans(d.scans || []); }
    } catch {}
    setLoading(false);
  }, [patient?.phone]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const vitalKeys = ['heart_rate', 'bp_systolic', 'bp_diastolic', 'spo2', 'respiratory_rate', 'stress_index', 'hrv', 'cardiac_output'];

  if (showScanner) {
    return (
      <FaceVitalsScanner
        patientPhone={patient?.phone || ''}
        patientName={patient?.name || ''}
        embedded
        onClose={() => { setShowScanner(false); fetchHistory(); }}
        onComplete={() => { setShowScanner(false); fetchHistory(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="patient-face-vitals-tab">
      {/* New Scan Button */}
      <Button
        data-testid="patient-start-scan-btn"
        onClick={() => setShowScanner(true)}
        className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white"
      >
        <Camera className="w-5 h-5 mr-2" /> Start New Face Vitals Scan (30s)
      </Button>

      {/* Screening Trends */}
      {trendData.length >= 2 && (
        <Card className="border-slate-200" data-testid="screening-trends-card">
          <CardContent className="p-0">
            <button
              className="w-full p-3 flex items-center justify-between text-left"
              onClick={() => setShowTrends(!showTrends)}
              data-testid="toggle-trends-btn"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-bold text-slate-700" style={{ fontFamily: 'Manrope' }}>Screening Trends</span>
                <Badge variant="outline" className="text-[10px]">{trendData.length} scans</Badge>
              </div>
              {showTrends ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showTrends && (
              <div className="px-3 pb-4 space-y-4">
                <div className="flex gap-1">
                  {['7d','30d','90d','all'].map(r => (
                    <button key={r} onClick={() => setTrendRange(r)} className={`px-2 py-0.5 rounded text-[10px] font-medium ${trendRange === r ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-500'}`}>
                      {r === 'all' ? 'All' : r === '7d' ? '7D' : r === '30d' ? '30D' : '90D'}
                    </button>
                  ))}
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-2 font-medium">Vitals Over Time <span className="text-[10px] text-emerald-600">(green = normal range)</span></p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <ReferenceArea y1={60} y2={100} fill="#2BB673" fillOpacity={0.08} />
                      <ReferenceLine y={100} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: 'High', position: 'insideTopRight', fontSize: 9, fill: '#F59E0B' }} />
                      <ReferenceLine y={60} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: 'Low', position: 'insideBottomRight', fontSize: 9, fill: '#F59E0B' }} />
                      <Line type="monotone" dataKey="heart_rate" name="Heart Rate" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line type="monotone" dataKey="spo2" name="SpO2 %" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line type="monotone" dataKey="stress" name="Stress" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {trendData.some(d => d.skin_score != null) && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2 font-medium">Face Screening Score</p>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Line type="monotone" dataKey="skin_score" name="Skin Health" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Scan History */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-3" style={{ fontFamily: 'Manrope' }}>
          Your Scan History ({scans.length})
        </h3>

        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-teal-600 mx-auto" />
          </div>
        ) : scans.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Heart className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No scans yet. Start your first scan above!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {scans.map(scan => {
              const v = scan.vitals || {};
              const isExpanded = expandedId === scan.id;
              const hasAttention = vitalKeys.some(k => getStatus(k, v[k]) !== 'normal');
              const scanDate = new Date(scan.created_at || new Date().toISOString());

              return (
                <Card key={scan.id} className={`border ${hasAttention ? 'border-amber-200' : 'border-slate-200'}`} data-testid={`patient-scan-${scan.id}`}>
                  <CardContent className="p-0">
                    <button
                      className="w-full p-3 flex items-center justify-between text-left"
                      onClick={() => setExpandedId(isExpanded ? null : scan.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${hasAttention ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                          {hasAttention ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm text-slate-800">
                              {hasAttention ? 'Needs Attention' : 'All Normal'}
                            </p>
                            <Badge variant="outline" className="text-[10px]">{scan.source === 'kiosk_checkin' ? 'Kiosk' : 'Self'}</Badge>
                          </div>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {scanDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} {scanDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-3 text-sm">
                          <span className="text-slate-700"><Heart className="w-3 h-3 inline mr-0.5 text-rose-400" />{v.heart_rate}</span>
                          <span className="text-slate-700"><Activity className="w-3 h-3 inline mr-0.5 text-red-400" />{v.bp_systolic}/{v.bp_diastolic}</span>
                          <span className="text-slate-700"><Droplets className="w-3 h-3 inline mr-0.5 text-blue-400" />{v.spo2}%</span>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-slate-100 pt-3 space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {vitalKeys.map(key => {
                            const meta = VITAL_META[key];
                            const val = v[key];
                            const status = getStatus(key, val);
                            const Icon = meta.icon;
                            return (
                              <div key={key} className={`rounded-lg p-2 border ${status === 'normal' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                                <div className="flex items-center gap-1 mb-0.5">
                                  <Icon className="w-3 h-3 opacity-60" />
                                  <span className="text-[10px] font-medium opacity-70">{meta.label}</span>
                                </div>
                                <p className="text-base font-bold" style={{ fontFamily: 'Manrope' }}>
                                  {key === 'bp_systolic' ? `${v.bp_systolic}/${v.bp_diastolic}` : val ?? '--'}
                                  <span className="text-[10px] font-normal ml-0.5 opacity-50">{key === 'bp_systolic' ? '' : meta.unit}</span>
                                </p>
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>Pulse: {v.pulse_regularity || '--'}</span>
                          <span>Vascular Age: {v.vascular_age || '--'} yrs</span>
                          <span>Signal: {v.signal_quality || '--'}%</span>
                        </div>

                        {(scan.ai_analysis && !scan.ai_analysis.error) && (
                          <Button size="sm" onClick={() => setAiDialog(scan)} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid={`patient-ai-btn-${scan.id}`}>
                            <Brain className="w-4 h-4 mr-1.5" /> AI Health Insights
                          </Button>
                        )}
                        {(scan.face_screening && !scan.face_screening.error) && (
                          <Button size="sm" onClick={() => setScreeningDialog(scan)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`patient-screening-btn-${scan.id}`}>
                            <Camera className="w-4 h-4 mr-1.5" /> Face Screening
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* AI Dialog */}
      <Dialog open={!!aiDialog} onOpenChange={() => setAiDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Brain className="w-5 h-5 text-teal-600" /> AI Health Insights
            </DialogTitle>
          </DialogHeader>
          {aiDialog?.ai_analysis && <AIContent ai={aiDialog.ai_analysis} />}
        </DialogContent>
      </Dialog>

      {/* Face Screening Dialog */}
      <Dialog open={!!screeningDialog} onOpenChange={() => setScreeningDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Camera className="w-5 h-5 text-indigo-600" /> 1Clin AI Face Screening
            </DialogTitle>
          </DialogHeader>
          {screeningDialog?.face_screening && <ScreeningContent screening={screeningDialog.face_screening} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AIContent({ ai }) {
  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-4 border ${ai.overall_status === 'Normal' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <p className="font-bold text-sm">{ai.overall_status}</p>
        <p className="text-sm text-slate-600 mt-1">{ai.summary}</p>
      </div>

      {ai.recommended_tests?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-500" /> Recommended Tests</p>
          <div className="space-y-2">
            {ai.recommended_tests.map((t, i) => (
              <div key={`el-${i}`} className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-violet-800">{t.test_name}</p>
                  <Badge variant="outline" className={`text-[10px] ${t.urgency === 'urgent' ? 'border-rose-300 text-rose-600' : 'border-amber-300 text-amber-600'}`}>{t.urgency}</Badge>
                </div>
                <p className="text-xs text-violet-600 mt-0.5">{t.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {ai.diet_plan && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Salad className="w-4 h-4 text-emerald-500" /> Diet Plan</p>
          <div className="space-y-2">
            {ai.diet_plan.foods_to_include?.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <p className="font-medium text-emerald-700 text-xs mb-1">Include</p>
                <ul className="text-xs text-emerald-600 space-y-0.5 list-disc pl-4">
                  {ai.diet_plan.foods_to_include.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                </ul>
              </div>
            )}
            {ai.diet_plan.foods_to_avoid?.length > 0 && (
              <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                <p className="font-medium text-rose-700 text-xs mb-1">Avoid</p>
                <ul className="text-xs text-rose-600 space-y-0.5 list-disc pl-4">
                  {ai.diet_plan.foods_to_avoid.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                </ul>
              </div>
            )}
            {ai.diet_plan.meal_suggestions?.length > 0 && (
              <div className="bg-sky-50 border border-sky-100 rounded-lg p-3">
                <p className="font-medium text-sky-700 text-xs mb-1">Meal Ideas</p>
                <ul className="text-xs text-sky-600 space-y-0.5 list-disc pl-4">
                  {ai.diet_plan.meal_suggestions.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                </ul>
              </div>
            )}
            {ai.diet_plan.hydration && (
              <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-lg p-2"><Droplets className="w-3 h-3 inline mr-1" />{ai.diet_plan.hydration}</p>
            )}
          </div>
        </div>
      )}

      {ai.lifestyle_recommendations?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><Dumbbell className="w-4 h-4 text-amber-500" /> Lifestyle Tips</p>
          <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
            {ai.lifestyle_recommendations.map((l, i) => <li key={`${l}-${i}`}>{l}</li>)}
          </ul>
        </div>
      )}

      {ai.follow_up && (
        <div className="bg-teal-50 border border-teal-100 rounded-lg p-3">
          <p className="text-xs text-teal-700"><Timer className="w-3 h-3 inline mr-1" /><b>Follow-up:</b> {ai.follow_up}</p>
        </div>
      )}
    </div>
  );
}

function ScreeningContent({ screening }) {
  const getSeverityColor = (severity) => {
    if (!severity) return 'slate';
    const s = severity.toLowerCase();
    if (s === 'none' || s === 'well-hydrated' || s === 'excellent') return 'emerald';
    if (s === 'mild' || s === 'mildly-dehydrated' || s === 'good') return 'amber';
    if (s === 'moderate' || s === 'dehydrated' || s === 'fair') return 'orange';
    if (s === 'severe' || s === 'poor') return 'rose';
    return 'slate';
  };
  const colorClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1 rounded-xl p-4 border bg-indigo-50 border-indigo-200 text-center">
          <p className="text-3xl font-bold text-indigo-700" style={{ fontFamily: 'Manrope' }}>{screening.overall_skin_health_score ?? '--'}</p>
          <p className="text-xs text-indigo-500 mt-1">Skin Health Score</p>
        </div>
        <div className="flex-1 rounded-xl p-4 border bg-violet-50 border-violet-200 text-center">
          <p className="text-3xl font-bold text-violet-700" style={{ fontFamily: 'Manrope' }}>{screening.ai_confidence ?? '--'}%</p>
          <p className="text-xs text-violet-500 mt-1">AI Confidence</p>
        </div>
      </div>
      <div className="rounded-xl p-4 border bg-slate-50 border-slate-200">
        <p className="text-sm text-slate-700">{screening.summary}</p>
      </div>
      {screening.screenings?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">Screening Results</p>
          <div className="space-y-2">
            {screening.screenings.map((s, i) => {
              const c = getSeverityColor(s.severity);
              return (
                <div key={i} className={`rounded-lg p-3 border ${colorClasses[c]}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold">{s.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] capitalize">{s.severity}</Badge>
                      <span className="text-xs font-bold opacity-70">{s.score}/100</span>
                    </div>
                  </div>
                  <p className="text-xs opacity-80">{s.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {screening.recommendations?.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">Recommendations</p>
          <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
            {screening.recommendations.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {screening.disclaimer && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
          <p className="text-[11px] text-amber-700 flex gap-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{screening.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
